/* BLOCKS 5 AND 6 OF JAY'S MOCKUP, IN ONE PLACE — 2026-09-29.

   They were written for the approver's panel. Max then asked for the same two blocks at the foot of
   the calculator, so whoever builds a quote sees what the approver will see before sending it
   ("thêm vào calculator một khối tóm tắt cuối trang... để người tạo thấy trước cái CEO sẽ thấy").

   SHARED, NOT COPIED, and that is the whole point of the request: two copies drift, and the day
   they drift the calculator promises a margin the approval screen does not show. One derivation
   (`cpFigures`) and one render, called from both screens.

   The figures come from `computeSku`, the calculator's own function, so nothing here recomputes
   anything — it only arranges.

   ONE LANGUAGE AT A TIME — Max, 2026-09-30: "chỉ khi nào chọn cái nút tiếng Hàn thì mới chuyển
   toàn bộ sang tiếng Hàn; cái mockup của Jay là để cho chúng ta biết thuật ngữ tiếng Hàn đúng là
   gì". His mockup printed both because it was showing us his vocabulary, not proposing a bilingual
   screen. So every label goes through i18n like the rest of the portal, and his Korean is what the
   Korean side says — which is the mockup doing its job rather than being copied literally.
*/
import React from 'react';
import { useTranslation } from 'react-i18next';
import { computeSku, fmtUSD, isCbd } from './pricingModel';
import FormulaHint from './FormulaHint';
import './PadBlocks.css';

export const n2 = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);

/* THE LINES A BENCHMARK IS COMPARED ON, in one place because two screens walk them: the calculator
   draws an input per line and a reason box beside each difference, and the approver's panel prints
   the same differences read-only. Two lists would drift the day a fourth line is added, and the
   drift would look like the approver being shown a smaller quote than the one that was built.
   `tk` is the i18n key for the label; `bench_<key>` is where the benchmark's own figure is stored. */
export const CP_BENCH_LINES = [
  { key: 'skin', tk: 'skin' },
  { key: 'cmt', tk: 'cmt' },
  { key: 'ohp', tk: 'ohp' },
  { key: 'frame', tk: 'frame' },
];

/* `$-357.45` is where fmtUSD lands on a negative, because the sign belongs to the number and the
   symbol is glued to its front. A loss has to read as one at a glance. */
export const money = (x) => {
  const v = n2(x);
  return v < 0 ? `−${fmtUSD(Math.abs(v))}` : fmtUSD(v);
};
/* Whole dollars, as the mockup rounds the order-level figures. */
export const money0 = (x) => {
  const v = Math.round(n2(x));
  const s = Math.abs(v).toLocaleString('en-GB');
  return v < 0 ? `−$${s}` : `$${s}`;
};
/* Label left, figure right, the whole row on one baseline — `hero` makes the figure the largest
   thing in its card, which is what the eye is meant to land on. */
export const Line = ({ l, r, hero, tone, dim, sub, spacer, fx, cls, title }) => (
  /* A SPACER IS THE SAME ROW WITH NOTHING IN IT, and that is the point: the two cards of a pair
     have different numbers of build-up lines, and the only way their totals land on one line is
     for the shorter card to be padded by rows built the same way. A height in CSS would have to
     be kept in step with this component's padding, border and line-height by hand. Hidden from a
     screen reader, which has no columns to read across. */
  <div className={`pad-line${hero ? ' hero' : ''}${dim ? ' dim' : ''}${tone ? ` ${tone}` : ''}${spacer ? ' spacer' : ''}${cls ? ` ${cls}` : ''}`}
    aria-hidden={spacer ? 'true' : undefined} title={title || undefined}>
    <span className="l">{spacer ? '\u00a0' : l}{sub ? <small>{sub}</small> : null}</span>
    <span className="r">{spacer ? '\u00a0' : r}{fx || null}</span>
  </div>
);

/* THE PRICE LEVERS AND THE QUOTE WITH ONE OPTION'S ANSWERS IN ITS PLACE — moved here 2026-09-30.

   They were private to the calculator, which is where options are BUILT. The approver's panel now
   has to price them too: Max, 2026-09-30, "tôi thấy 3 option ko tương thích với 3 card bên CEO" —
   a quote can carry three costings and the CEO was shown one of them, with nothing recording which
   one was approved.

   Shared rather than copied for the usual reason, sharper here than anywhere: an option is a set
   of OVERRIDES, and a second implementation of "which fields does this lever replace, and what
   does it clear" would mean the approver pricing an option the builder never built. */
export const OPT_CONDITIONS = [
  /* THE LEVERS, AS TICKS — Max, 2026-09-22: "cho tôi được tick chọn giống như thiết kế trước nhé",
     with the mockup's own grid beside it. Checkboxes in two columns, each with its field, the field
     dead until its box is ticked.

     ORDER QUANTITY IS NOT ONE OF THEM — "bỏ cái Order quantity đi dùm". It went out, came back with
     the grid, and is out again by his word; the quantity is asked for in block 1 and every option
     is priced at the quote's. An option that needs a different run is a different quote.

     ONE THING FROM THE ROUND TRIP IS KEPT: ticking opens an EMPTY field. Seeding it with the
     quote's own figure is what made the first version a no-op — "chọn như ko chọn vậy chọn chi ta"
     — because an option repeating the quote's answer IS the quote. The quote's figure is the grey
     placeholder instead, so the box asks what this option says rather than answering for it. */
  /* THE PRICE LEVERS, CHOSEN — Max, 2026-09-22: "chỗ này là chỗ chọn những cái làm thay đổi giá
     chứ ko phải là điền". Not a form of six boxes: a list of what an option MAY change, picked one
     at a time.

     And picking one leaves its box EMPTY. Seeding it with the quote's own figure is what made the
     old tick a no-op — "chọn như ko chọn vậy chọn chi ta" — because an option that repeats the
     quote's answer is the quote. An empty box is the question "what does this option say instead",
     and until it is answered the option is still the quote, which the card says in words. */
  { key: 'marginBasis', tk: 'optBasis', kind: 'basis' },
  { key: 't0Profit', tk: 'optT1Profit', kind: 'number', unit: '%', step: '0.1' },
  /* THE SAME QUANTITY BLOCK 5 ASKS FOR — Max, 2026-09-24: "2 chỗ phần trăm này phải link với nhau".
     It used to be `t1Profit`, the profit HALF, while block 5 now asks for the whole T2 ratio; the
     same number typed in the two places meant two different things and priced two different
     quotes. One field, one meaning. `clears` goes with it because `tierParts` prefers the halves
     to the total, so an option that set the ratio while the quote's 15 and 30 stood would have
     been ignored — a lever that moves nothing. */
  { key: 't1Ratio', tk: 'optT2Ratio', kind: 'number', unit: '%', step: '0.1', clears: ['t1Oh', 't1Profit'] },
  /* CBD ONLY: the option's margin on the selling price (T2) — Tina, 2026-10-23: the sales
     representative prepares several options with different margin rates for YL to choose from. */
  { key: 'cbdMargin', tk: 'optCbdMargin', kind: 'number', unit: '%', step: '0.5', cbdOnly: true },
  { key: 'premium', tk: 'optPremium', kind: 'number', unit: '%', step: '0.1' },
  /* LAST, and Max moved Collab premium above it on 2026-09-24: the three narrow percentage levers
     read as one row of the same kind of answer, and a block that spans the panel underneath them
     is the end of the list rather than something wedged into the middle of it. */
  { key: 'extraOff', tk: 'optExtraOff', kind: 'items', always: true },
  /* WHO CARRIES THE EXTRA IS NOT A LEVER HERE, and the one that was briefly added (2026-09-30)
     came off on 2026-10-01 with the merge it served. An option changes the COSTING; A to D decides
     who carries the extra WITHIN a costing, on the approver's screen, where the quote's options do
     not appear at all — Jay, 29/09: "Keep them in the calculator as a working tool. They just
     don't appear on Pricing Approval." A tick here that nothing downstream reads is a control that
     moves no number, which is worse than no control. */
  /* The standalone Short-lead surcharge lever came off on 2026-09-24 — Max: "bỏ cái Short-lead
     surcharge đi". It is one of the Extra cost 1 lines above, where it can be dropped like any
     other, and two controls over one figure is how a screen comes to disagree with itself. */
];
export const withOption = (sku, opt) => {
  const live = OPT_CONDITIONS.filter((c) => (c.always || opt.on[c.key])
    && opt.values[c.key] !== undefined && opt.values[c.key] !== '');
  const values = { ...sku.values };
  // A lever that supersedes other fields has to clear them, or the ones it replaced win.
  live.forEach((c) => (c.clears || []).forEach((k) => { values[k] = ''; }));
  live.forEach((c) => { values[c.key] = opt.values[c.key]; });
  return { ...sku, values };
};

/* WHAT AN OPTION DEPARTS FROM THE QUOTE IN, in words — lifted out of the calculator 2026-09-30
   and made PURE so the approver's screen can say it too. It was a closure over the component's
   own `v` and `calc`; those are arguments now and nothing else changed.

   Max, 2026-09-30: the calculator's option cards say what each one changes and what it is worth,
   and the approver was offered three chips with nothing but a number in the name. A chooser whose
   choices are indistinguishable is not a chooser.

   A lever that has been picked but left EMPTY is not a departure — it is a question nobody has
   answered yet — and neither is one whose value matches the quote's. That is why the options on
   CP-000087 read as "same as the quote": they set the basis and the premium to what the quote
   already says. */
export const optDepartures = (values, opt) => {
  const quoteVal = (x) => (x.kind === 'basis' ? (values[x.key] || 'base') : String(values[x.key] ?? ''));
  return OPT_CONDITIONS.filter((x) => (x.always || opt.on[x.key])
    && opt.values[x.key] !== undefined && opt.values[x.key] !== ''
    && String(opt.values[x.key]) !== quoteVal(x));
};
/* `skip` leaves a lever out of the sentence. No caller needs it since the split lever came off an
   option (2026-10-01), and it is kept because it costs one line and the alternative — each caller
   filtering the list it was handed — is the drift this function exists to prevent. */
export const optDepartureText = (values, opt, t, items, skip) => {
  const diff = optDepartures(values, opt).filter((x) => !(skip || []).includes(x.key));
  if (!diff.length) return null;
  return diff.map((x) => {
    const val = opt.values[x.key];
    if (x.kind === 'basis') {
      return `${t('pricing.optBasis')} ${val === 'buying' ? t('pricing.marginBasisBuying') : t('pricing.marginBasisBase')}`;
    }
    /* The dropped lines are NAMED, not counted. "2 dropped" makes somebody open the option to find
       out which two, and the whole point of this line is to answer that without opening it. */
    if (x.kind === 'items') {
      const off = (Array.isArray(val) ? val : []).filter((k) => (items || {})[k] !== undefined);
      if (!off.length) return null;
      return `${t('pricing.optExtraOffLbl')} ${off.map((k) => t(`pricing.${k === 'moq' ? 'moqCharge' : k}`)).join(', ')}`;
    }
    const shown = `${x.unit === '$' ? '$' : ''}${x.unit === 'EA' ? (Number(val) || 0).toLocaleString('en-GB') : val}${x.unit === '%' ? '%' : (x.unit === 'EA' ? ' EA' : '')}`;
    return `${t(`pricing.${x.tk}`)} ${shown}`;
  }).filter(Boolean).join(' \u00b7 ');
};

/* ONE DERIVATION for both screens. Everything either of them prints about cost, price or margin
   comes out of here, so a change of rule or of convention reaches the two together or not at all. */
export const cpFigures = (sku, rules) => {
  if (!sku) return null;
  const c = computeSku(sku, rules);
  const v = sku.values || {};
  const benchFactory = n2(v.bench_skin) + n2(v.bench_cmt) + n2(v.bench_ohp);
  const benchFrame = n2(v.bench_frame);
  const benchBase = benchFactory + benchFrame;
  return {
    c,
    qty: n2(c.qty) || 1,
    benchFactory,
    benchFrame,
    benchBase,
    hasBench: benchBase > 0,
    extra: n2(c.excluded),
    partnerUnit: n2(c.partnerPrice),
    msrp: n2(c.msrp),
    base: n2(c.markupBase),
    total: n2(c.buyingCost),
    partnerPct: n2(rules?.partner_ratio_pct),
    ohPct: n2(rules?.tier_oh_pct),
    /* The two typed figures the MSRP is built from. Block 6 states the build-up rather than
       landing on a Target MSRP nobody can trace. */
    inlineRrp: n2(v.inlineRrp),
    premium: n2(v.premium),
    // The SKU's own name, so an order-level card can still say which style a figure belongs to.
    name: String(sku.name || '').trim(),
  };
};

/* The two grounds, named once. On the panel it is sticky so a figure halfway down still says which
   side it is on; the calculator's summary is short enough that it never scrolls away from it. */
export const CpColHead = ({ src }) => {
  const { t } = useTranslation();
  return (
    <div className="pad-colhead">
      <div className="c">{t('pricing.padCollab')} <small>{t('pricing.padThisQuote')}</small></div>
      <div className="i">
        {t('pricing.padInline')} <small>{t('pricing.padCompareOnly')}</small>
        {/* WHERE THE BENCHMARK CAME FROM. On the calculator it is printed inside block 2's card,
            which the approver's panel folds away — so the panel passes it here and the one fact an
            approver needs to weigh a comparison stops being lost with the card. */}
        {src ? <em className="pad-src" title={src}>{t('pricing.benchSrc')} {src}</em> : null}
      </div>
    </div>
  );
};

export const CpTotalCost = ({ fig }) => {
  const { t } = useTranslation();
  const { c, qty, base, total, extra, benchFactory, benchFrame, benchBase, hasBench } = fig;
  /* On the CBD method the factory half is the workbook's Base price, which carries the handling fee
     and the local transport, so the two lines still add up to the base printed under them. */
  const factoryLine = c.cbd ? c.cbd.base : c.factoryCost;
  const frameLine = c.cbd ? c.cbd.frame : c.frameCost;
  return (
    <div className="pad-block">
      <div className="pad-btitle">
        <h4>{t('pricing.padB5')}</h4><span className="pad-kind">{t('pricing.padKindCompare')}</span>
      </div>
      <div className="pad-pair">
        <div className="pad-card c">
          <p className="pad-cap">{t('pricing.padCollab')}</p>
          <Line l={c.cbd ? t('pricing.cbdBase') : t('pricing.padFactoryN')} r={money(factoryLine)} />
          <Line l={t('pricing.padFrameN')} r={money(frameLine)} />
          <Line l={t('pricing.padBase')}
            sub={t('pricing.padBaseFrom', { f: money(factoryLine), r: money(frameLine) })}
            r={money(base)} />
          <Line l={t('pricing.padExtraN')} r={<>{money(extra)}<small>{total ? ` ${t('pricing.padPctOfTotal', { n: Math.round((extra / total) * 100) })}` : ''}</small></>} />
          <Line hero l={t('pricing.padTotal')} r={money(total)} />
          <Line l={t('pricing.padOrder', { qty })} r={money(total * qty)} />
        </div>
        <div className="pad-card i">
          <p className="pad-cap">{t('pricing.padInline')}</p>
          <Line l={t('pricing.padFactory')} r={hasBench ? money(benchFactory) : '—'} />
          <Line l={t('pricing.padFrame')} r={hasBench ? money(benchFrame) : '—'} />
          <Line l={t('pricing.padBase')} r={hasBench ? money(benchBase) : '—'} />
          <Line l={t('pricing.padExtra')} r={<>—<small> {t('pricing.padNoneWord')}</small></>} />
          <Line hero l={t('pricing.padTotal')} r={hasBench ? money(benchBase) : '—'} />
        </div>
        {hasBench && (
          <div className="pad-gap">
            <div className="pad-eqrow">
              {/* Interpolated whole rather than assembled around a <b>: Korean does not put the
                  figure where English does, and a sentence split around a bold tag can only be
                  translated by keeping the English word order. */}
              <span>{t('pricing.padMultBase', { x: (base / benchBase).toFixed(2) })}</span>
              <span>{t('pricing.padMultAll', { x: (total / benchBase).toFixed(2) })}</span>
              <span className="pad-lead">{t('pricing.padLead')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* BLOCK 6 ON THE CBD METHOD — Linda's "Calculation" sheet as the approver reads it: what the factory
   charges, what HPAC adds, the two prices and what HPAC keeps, per unit and over the order. There
   is no partner-at-50%-of-MSRP channel here: on this method T2 IS the price the partner pays. */
const CpCbdPrice = ({ fig }) => {
  const { t } = useTranslation();
  const { c, qty } = fig;
  const k = c.cbd;
  const x = (1 + (k.markupPct || 0) / 100).toFixed(2);
  const rowU = (l, u, opts = {}) => (
    <tr className={opts.cls || ''}>
      <td>{l}{opts.sub ? <small> {opts.sub}</small> : null}</td>
      <td>{u === null ? '—' : (opts.pct ? u : money(u))}</td>
      <td>{u === null || opts.pct ? '' : money0(u * qty)}</td>
    </tr>
  );
  return (
    <div className="pad-block pad-cbd">
      <div className="pad-btitle">
        <h4>{t('pricing.padB6')}</h4>
        <span className="pad-kind">{t('pricing.methodCbd')}</span>
      </div>
      <table className="pad-ch pad-cbd-tt">
        <thead>
          <tr><th /><th>{t('pricing.padPerUnitHead')}</th><th>{t('pricing.padOrderHead', { qty })}</th></tr>
        </thead>
        <tbody>
          {rowU(t('pricing.cbdBaseFrame'), k.base + k.frame)}
          {rowU(t('pricing.cbdOh', { pct: k.ohPct }), k.oh)}
          {rowU(t('pricing.cbdMoq'), k.moq, { sub: k.absorbMoq ? t('pricing.cbdAbsorbed') : '' })}
          {rowU(t('pricing.cbdSl'), k.sl, { sub: k.absorbSl ? t('pricing.cbdAbsorbed') : '' })}
          {rowU(t('pricing.cbdFactory'), k.factory, { cls: 'sub' })}
          {rowU(t('pricing.cbdT1'), k.t1)}
          {rowU(t('pricing.cbdT2'), k.t2, { cls: 'tot', sub: t('pricing.cbdT2Sub', { x }) })}
          {rowU(t('pricing.cbdGp'), k.gp, { cls: k.gp < 0 ? 'bad' : 'good' })}
          {rowU(t('pricing.cbdMargin'), k.margin === null ? null : `${(k.margin * 100).toFixed(1)}%`, { pct: true })}
        </tbody>
      </table>
      {fig.msrp > 0 && (
        <p className="pad-note">{t('pricing.cbdMsrpNote', { msrp: money(fig.msrp), x: k.t2 ? (fig.msrp / k.t2).toFixed(2) : '—' })}</p>
      )}
    </div>
  );
};

export const CpPriceMargin = ({ fig }) => {
  const { t } = useTranslation();
  if (fig?.c?.cbd) return <CpCbdPrice fig={fig} />;
  const { msrp, partnerUnit, base, total, extra, partnerPct, benchBase, hasBench,
    inlineRrp, premium } = fig;
  const pct = (x, of) => (of ? ` ${Math.round((x / of) * 100)}%` : '');
  const ownAfter = msrp - total;
  const parAfter = partnerUnit - total;
  const inlineAfter = inlineRrp - benchBase;
  /* A CHANNEL TABLE, drawn the same way on either ground so the two can be read across. The
     inline side has one channel: it is not sold to partners, which is a fact worth stating rather
     than an empty column. */
  const Channels = ({ sell, cost, ex, headR, subR, parSell, parCost, parEx }) => (
    <table className="pad-ch">
      <thead>
        <tr>
          <th />
          <th>{t('pricing.padOwnRetail')}<small>{subR}</small></th>
          <th className={parSell === null ? 'dim' : ''}>{headR}<small>{parCost}</small></th>
        </tr>
      </thead>
      <tbody>
        <tr><td>{t('pricing.padWeSell')}</td><td>{money(sell)}</td>
          <td>{parSell === null ? '—' : money(parSell)}</td></tr>
        <tr><td>{t('pricing.padBase')} <small>{t('pricing.padBaseSub')}</small></td>
          <td>−{money(cost)}</td><td>{parSell === null ? '—' : `−${money(cost)}`}</td></tr>
        <tr className="sub">
          <td>{t('pricing.padMarginPre')}</td>
          <td className="good">{money(sell - cost)}<small>{pct(sell - cost, sell)}</small></td>
          <td className={parSell === null ? '' : 'good'}>
            {parSell === null ? '—' : <>{money(parSell - cost)}<small>{pct(parSell - cost, parSell)}</small></>}
          </td>
        </tr>
        <tr><td>{ex ? t('pricing.padExtraN') : t('pricing.padExtra')}</td>
          <td>{ex ? `−${money(ex)}` : '—'}</td>
          <td>{parSell === null || !parEx ? '—' : `−${money(parEx)}`}</td></tr>
        <tr className="tot">
          <td>{t('pricing.padMarginPost')}</td>
          <td className={sell - cost - (ex || 0) < 0 ? 'bad' : 'good'}>
            {money(sell - cost - (ex || 0))}<small>{pct(sell - cost - (ex || 0), sell)}</small>
          </td>
          <td className={parSell === null ? '' : (parSell - cost - (parEx || 0) < 0 ? 'bad' : 'good')}>
            {parSell === null ? '—'
              : <>{money(parSell - cost - (parEx || 0))}<small>{pct(parSell - cost - (parEx || 0), parSell)}</small></>}
          </td>
        </tr>
      </tbody>
    </table>
  );
  return (
    <div className="pad-block">
      <div className="pad-btitle">
        <h4>{t('pricing.padB6')}</h4>
        <span className="pad-kind">{t('pricing.padKindCompare')}</span>
      </div>
      <div className="pad-pair">
        <div className="pad-card c">
          <p className="pad-cap">{t('pricing.padCollab')}</p>
          {/* THE MSRP IS BUILT, NOT DECLARED: the inline retail price is the thing it starts from
              and the premium is the only lever on it, so a reader can see where $120.95 came
              from instead of taking it. */}
          <Line l={t('pricing.padInlineRrp')} r={money(inlineRrp)} />
          <Line l={t('pricing.padPremium')} r={`+${premium}%`} />
          <Line hero l={t('pricing.padTargetMsrp')} r={money(msrp)} />
          <Channels sell={msrp} cost={base} ex={extra}
            subR={t('pricing.padOwnRetailSub')}
            headR={t('pricing.padPartnerWs')}
            parCost={t('pricing.padPartnerWsSub', { p: partnerPct })}
            parSell={partnerUnit} parEx={extra} />
          {parAfter < 0 && (
            <div className="pad-callout">
              {t('pricing.padCallout', { pct: Math.round((ownAfter / msrp) * 100) })}
            </div>
          )}
        </div>
        <div className="pad-card i">
          <p className="pad-cap">{t('pricing.padInline')}</p>
          {/* The collab side builds its MSRP in three lines; the inline product's retail price is
              both the start and the total, so it has one. The two headline figures only sit on the
              same line if the shorter card makes up the difference — which is how the mockup draws
              it, and what lets the tables underneath be read across at all. */}
          {[...Array(2)].map((_, i) => <Line key={`sp${i}`} spacer />)}
          <Line hero l={t('pricing.padInlineRrp')} r={hasBench ? money(inlineRrp) : '—'} />
          <Channels sell={inlineRrp} cost={benchBase} ex={0}
            subR={t('pricing.padOwnRetailRef')}
            headR={t('pricing.padPartnerWs')}
            parCost={t('pricing.padPartnerNa')}
            parSell={null} parEx={0} />
        </div>
        <div className="pad-gap">
          <div className="pad-eqrow">
            <span>{t('pricing.padGapOwn', {
              c: Math.round((ownAfter / msrp) * 100),
              i: hasBench && inlineRrp ? Math.round((inlineAfter / inlineRrp) * 100) : '—',
            })}</span>
            <span>{parAfter < 0
              ? t('pricing.padGapPartnerBelow', { amt: money(Math.abs(parAfter)) })
              : t('pricing.padGapPartnerOk', { pct: Math.round((parAfter / (partnerUnit || 1)) * 100) })}</span>
            <span className="pad-lead">{t('pricing.padToB7', { amt: money(extra) })}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------------------------------------
   BLOCK 7 — THE CARDS A QUOTE CAN BE CLOSED ON

   Max, 2026-09-30: "tui đã nói là option và card là 1. Nếu user tạo phiếu thì chọn mặc định 3 card,
   nếu cần thì user tạo thêm card/option khác thôi." — and Jay's own instruction from the start:
   "who carries the extra (A–D)". ONE FLAT LIST.

   It was two questions on two axes — pick a costing, then pick a split — so three cards on screen
   were three splits of one costing and every other costing was behind a chooser somewhere else.
   Nothing on the screen said so, which is why the same figures kept reading as wrong.

   A, B and C are the three ways to split ONE extra and cost nothing to compute. Every option the
   quote carries is one more card in the same list, priced on its own costing and carrying its own
   answer to the same question — which is why that answer became a lever on the option itself. D is
   terms written by hand, and is last because it is the answer when none of the priced ones will do.

   SHARED, like blocks 5 and 6 above: the approver decides on this row and the calculator draws it
   read-only at the foot, so the person building a quote sees the cards it will be judged on. Two
   copies of this arithmetic is how the two screens come to disagree about one quote.

   EVERY CARD IS AN ORDER-LEVEL FIGURE. `extra_option` is stored once on the project row, so the
   decision covers every SKU at once; computed on SKU #1 a quote of eight would have shown an
   eighth of the argument. */

/* Order-level totals for one costing. `unit`, `msrp` and `eu` are carried as an AGREEMENT rather
   than a sum: null until the first SKU answers, that answer while every SKU repeats it, NaN the
   moment two differ. A per-unit figure printed across SKUs that do not share it is a price nobody
   pays, and an average hides that they differ — the card draws it only while it is finite, which
   is one test for both cases. */
const cardTotals = (skus, rules, mutate) => skus
  .map((sk) => cpFigures(mutate ? mutate(sk) : sk, rules))
  .filter(Boolean)
  .reduce((a, f) => ({
    extra: a.extra + f.extra * f.qty,
    cost: a.cost + f.total * f.qty,
    rev: a.rev + f.partnerUnit * f.qty,
    /* THE ORDER AT MSRP — Jay, 2026-10-22, for Young: "Please show the overall Sales Revenue (QTY x
       MSRP) in each cards". Summed SKU by SKU, never `msrp × qty` off the agreement above: three
       SKUs at three MSRPs have no single MSRP to multiply, and the order still has one total. `rev`
       is a different figure — the order at the PARTNER price, which is what Helinox is paid. */
    retail: a.retail + f.msrp * f.qty,
    /* AND PER STYLE — Max, same day: "total revenue of each style … là theo từng SKU chứ". Each
       part is one SKU's qty, MSRP and name, in the quote's order, so the card can print a line per
       SKU under the order's total and the total is visibly their sum. */
    retailParts: [...a.retailParts, [f.qty, f.msrp, f.name]],
    oh: a.oh + f.base * f.qty * (f.ohPct / 100),
    qty: a.qty + f.qty,
    unit: a.unit === null ? f.partnerUnit : (a.unit === f.partnerUnit ? a.unit : NaN),
    msrp: a.msrp === null ? f.msrp : (a.msrp === f.msrp ? a.msrp : NaN),
    eu: a.eu === null ? f.extra : (a.eu === f.extra ? a.eu : NaN),
  }), { extra: 0, cost: 0, rev: 0, retail: 0, retailParts: [], oh: 0, qty: 0, unit: null, msrp: null, eu: null });

/* Who carries how much of the extra. The three the portal always offers, and the same three an
   option picks from — one table, so a card called B means the same thing wherever it is drawn. */
/* A, B and C ARE THREE POINTS ON ONE LINE: what share of the extra the partner carries, 100, 0 and
   50. Written as the shares rather than as three functions, because the approver's own card is a
   fourth point on the same line and a table of lambdas cannot hold "whatever they typed". */
export const CP_SPLITS = { A: 100, B: 0, C: 50 };

const priceCard = (tot, split, pctIn) => {
  const pct = Number.isFinite(pctIn) ? pctIn : (CP_SPLITS[split] ?? CP_SPLITS.A);
  const cut = (x) => (n2(x) * pct) / 100;
  const billed = cut(tot.extra);
  const gross = tot.rev + billed - tot.cost;
  const net = gross - tot.oh;
  const billedUnit = cut(tot.eu);
  return {
    ...tot,
    split,
    pct,
    billed,
    gross,
    net,
    margin: tot.rev ? net / tot.rev : null,
    billedUnit,
    allIn: tot.unit + billedUnit,
  };
};

export const cpCards = (skus, rules, t, items, only) => {
  const list = skus || [];
  /* THE CARDS ARE PRICED ON THE SKUs THAT ARE STILL IN — Max, 2026-10-04. `only` is the list of
     SKUs the approver has not rejected; left out it is every SKU, which is what the calculator
     passes and what a quote with no per-SKU verdict means. A card that went on folding eight SKUs
     after three were rejected would describe an order nobody approved. */
  const live = only && only.length ? only : list;
  if (live.some((sk) => isCbd(sk && sk.values))) return cpCbdCards(live, rules, t, items);
  const ord = cardTotals(live, rules);
  /* ONE ROW AGAIN, AND THE OPTIONS ARE IN IT — Jay, in the meeting of 2026-10-04 relayed by Max:
     "đưa option 1 và 2 lên trên luôn là thành dạng D option 1 và E option 2". This is the shape of
     2026-09-30/10-01 (commit c1ef47f) coming back after a fortnight in two rows, and the history
     of the argument is worth keeping because it is the same argument both ways.

     An option and a split answer DIFFERENT QUESTIONS — an option changes the COSTING (the T1
     profit, the T2 ratio, the premium, a dropped extra line) and A/B/C decide WHO CARRIES the
     extra within a costing — which is why they were separated in the first place. What settles it
     the other way is that the approver is not asked two questions: every card on this row is one
     complete way to close the quote, and the only act is choosing one of them. An option card is
     priced at A and says so on its face, so what it means is stated on the card rather than by the
     row it sits in.

     WHERE THEY CAME FROM IS MARKED ON EACH CARD, not on the row — Max, same message: "làm sao để
     phân biệt được là ABC là mặc định còn các option kia là do phía user offer thêm". A band
     spanning the row was the obvious drawing and it is the wrong one: this grid folds to two
     columns at 1240px and to one on a phone, so a band would end up over the wrong cards. A chip
     on the card survives every width. */
  const cards = [
    ...['A', 'B', 'C'].map((k) => ({
      key: k, name: t(`pricing.padOpt${k}`), who: t(`pricing.padWho${k}`),
      src: 'portal', srcLabel: t('pricing.padSrcPortal'), ...priceCard(ord, k),
    })),
    /* The quote's own costings, between the three splits and the terms card: they are priced
       answers, which is what the first four are, and the hand-written one is still the answer when
       none of the priced ones will do. */
    ...cpOptionCards(live, rules, t, items || {}).map((o) => ({
      ...o, src: 'quote', srcLabel: t('pricing.padSrcQuote'),
    })),
    /* THE TERMS CARD, last. It carries no figures of its own because it is not a price the portal
       can work out: it is the approver saying the answer is none of these, and here is what it is.
       A version that priced itself on a share the approver typed was built and reverted the same
       day (sql/20261001a, 20261001b) — Max: "làm theo ý Jay nha". */
    {
      key: 'TERMS', name: t('pricing.padOptTerms'), who: t('pricing.padOptTermsSub'), hand: true,
      src: 'approver', srcLabel: t('pricing.padSrcApprover'),
    },
  ];
  /* THE LETTER IS THE PLACE IN THE ROW, not the key. A, B, C, then D, E… for the quote's options
     and the next for the terms card — which is exactly the row Jay writes when a quote carries no
     options at all (A–D). Kept as a derivation rather than baked into the names so that the names
     stay translatable and the record, the button and the card cannot drift apart. `key` is what is
     STORED: 'A'/'B'/'C', 'O1'..'O99' and 'TERMS', never the letter, because the letter is
     positional and the same letter means different cards on two quotes. */
  cards.forEach((c, i) => { c.letter = String.fromCharCode(65 + i); });
  /* THREE ACROSS, and the rest wrap onto the next line of the same list — Max, 2026-10-05:
     "1 hàng 3 card thui". MEASURED both ways: the quote panel is capped at 1180px whatever the
     screen, so the row is 1144px and six cards are 179px each — at that width every label wraps
     to three lines and the headline figure had to be stacked under its own label to stop being
     drawn over it. At three the card is 365px, which is the width the block was designed at.
     IT IS STILL ONE LIST AND ONE ALPHABET. Wrapping is a line break, not a second row of cards:
     the letters run A, B, C, then D, E, F on the line below, and with the usual two options that
     puts the portal's three on one line and the requester's two plus the terms card on the next —
     which is the grouping the origin chips state anyway. */
  return { cards, cols: Math.min(cards.length, 3), ord };
};

/* THE ROW ITSELF. `foot` is what goes under a card — the approver's Select button, nothing on the
   calculator — so the one screen that can decide is the only one that draws a control. */
export const CpCardRow = ({ cards, cols, decided, ohPct, foot, rowClass }) => {
  const { t } = useTranslation();
  return (
    /* The count goes in as a CUSTOM PROPERTY, not as `gridTemplateColumns`: an inline declaration
       beats a stylesheet rule including the ones inside a media query, so setting the columns here
       directly would pin six cards at six across on a phone. */
    /* `rowClass` NAMES THE ROW. There were two for a fortnight — the fixed cards and the quote's
       own costings — and with one class between them nothing on the page or in a check could tell
       which cards it was looking at; the first thing that went wrong was a count of "the four"
       that returned six. They are one row again since 2026-10-04 and the name went with them:
       `pad-opts-all`, because `pad-opts-four` was a count and the count is no longer four. */
    <div className={`pad-opts ${rowClass || ''}`} style={{ '--pad-cols': cols }}>
      {cards.map((o) => (
        <div key={o.key}
          className={`pad-opt ${o.hand ? 'd' : ''} ${o.opt ? 'optcard' : ''} ${decided === o.key ? 'pick' : ''}`}>
          {/* WHERE THE CARD CAME FROM, on the card — the three splits the portal offers on every
              quote, the costings the requester added, and the terms the approver writes. Marked
              per card rather than per row because the grid folds to two columns and then to one,
              and a heading spanning the row would end up over the wrong cards. */}
          {/* `pad-origin`, not `pad-src`: that class is already the benchmark's source line a few
              hundred lines up, and two meanings on one name is how a stylesheet comes to restyle
              something nobody was looking at. */}
          {!!o.srcLabel && <div className={`pad-origin o-${o.src}`}>{o.srcLabel}</div>}
          {/* A, B and C carry their letter inside their own NAME, so it is not added twice. Every
              other card takes the letter its place in the row gives it. Keyed on `key` and not on
              a pattern in the name: an option the requester calls "A lighter frame" would have
              matched a test for a leading capital and silently lost its own letter. */}
          <h5>
            {['A', 'B', 'C'].includes(o.key) && !o.cbd ? o.name
              : <><b className="pad-ltr">{o.letter}</b> · {o.name}</>}
          </h5>
          {!!o.diff && <div className="pad-optdiff">{o.diff}</div>}
          {/* THE TERMS BOX IS PER UNIT, so it is drawn only while there IS one unit price: three
              SKUs at three different partner prices have no single figure to print, and the first
              one's under a heading that says "partner pays all-in" is a number the partner never
              pays. An option card prints its OWN, because its costing can move the MSRP and the
              unit price as well as the extra. */}
          {!o.hand && !o.cbd && Number.isFinite(o.unit) && Number.isFinite(o.msrp) && (
            <div className="pad-terms">
              <span>{t('pricing.padMsrp')} <b>{money(o.msrp)}</b></span>
              <span>{t('pricing.padPartnerUnit')} <b>{money(o.unit)}</b></span>
              <span>{t('pricing.padExtraBilledUnit')} <b>{Number.isFinite(o.billedUnit)
                ? `${money(o.billedUnit)}${t('pricing.padPerUnit')}` : '—'}</b></span>
              <span className="allin">{t('pricing.padAllIn')} <b>{Number.isFinite(o.allIn)
                ? `${money(o.allIn)}${t('pricing.padPerUnit')}` : '—'}</b></span>
            </div>
          )}
          <div className={`pad-who ${o.hand ? 'dnote' : ''}`}>
            {!!o.whoHead && <b className="pad-whohead">{o.whoHead}</b>}
            {o.who}
          </div>
          {o.cbd && <CbdCardLines o={o} />}
          {!o.hand && !o.cbd && (() => {
            /* THE ⓘ ON EVERY LINE — Max, 2026-10-01: "nhớ cho các chữ i vào để giải thích các công
               thức tính nhé". The calculator has carried these since 2026-09-24 and this row, which
               is where the money is actually decided, had none: six figures and no way to ask any
               of them where it came from.
               EVERY HINT READS THE CARD'S OWN STORED NUMBERS and never works one out again — the
               rule the calculator's own note sets down, because a tooltip that recomputes is a
               second implementation of the rule and the day the two disagree the tooltip is the one
               people believe. */
            const money2 = (x) => n2(x).toFixed(2);
            const hint = (title, rule, work) => (
              <FormulaHint title={title} rule={rule} work={work} result="" align="right" />
            );
            /* The working of the revenue line: one product per SKU, as many as there are — a quote of
               eight shows eight, which is what "added up over the order" has to be checked against. */
            const retailWork = (o.retailParts || []).map(([q, m]) => `${n2(q)} × ${money2(m)}`);
            return (
              <>
                <Line l={t('pricing.padRevenue')} r={money0(o.retail)}
                  fx={hint(t('pricing.padRevenue'), t('pricing.fxRevenueRule'),
                    retailWork.length > 1 ? [retailWork.join(' + ')] : retailWork)} />
                {/* ONE LINE PER STYLE under the order's total — what Young reviews (Tina via Jay).
                    Only when there is more than one SKU: with one, the total above IS that style.
                    The name gives way to the figure on a narrow card and is whole on the hover. */}
                {(o.retailParts || []).length > 1 && o.retailParts.map(([q, m, nm], i) => {
                  const label = nm || t('pricing.padSkuN', { n: i + 1 });
                  return (
                    <Line key={`rev${i}`} cls="skurev" title={`${label} — ${n2(q)} × ${money(m)}`}
                      l={<><span className="skurev-nm">{label}</span><small>{n2(q)} × {money(m)}</small></>}
                      /* Whole dollars like every figure on the card (Max: "0 chữ số thập phân nhé").
                         Each line is rounded on its own, so the lines can sum a dollar off the total. */
                      r={money0(q * m)} />
                  );
                })}
                <Line l={t('pricing.padBilled')} r={money0(o.billed)}
                  fx={hint(t('pricing.padBilled'), t('pricing.fxBilledRule'),
                    [`${money2(o.extra)} × ${n2(o.pct)}%`])} />
                <Line l={t('pricing.padExtraAbsorbed')} r={money0(o.extra - o.billed)}
                  fx={hint(t('pricing.padExtraAbsorbed'), t('pricing.fxAbsorbedRule'),
                    [`${money2(o.extra)} − ${money2(o.billed)}`])} />
                <Line l={t('pricing.padGross')} sub={t('pricing.padGrossSub')} r={money0(o.gross)}
                  fx={hint(t('pricing.padGross'), t('pricing.fxGrossRule'),
                    [`${money2(o.rev)} + ${money2(o.billed)} − ${money2(o.cost)}`])} />
                <Line dim l={t('pricing.padOh', { pct: ohPct })} r={`−${money0(o.oh).replace('−', '')}`}
                  fx={hint(t('pricing.padOh', { pct: ohPct }), t('pricing.fxOhRule'),
                    [`${money2(o.oh / (ohPct / 100))} × ${n2(ohPct)}%`])} />
                <Line hero tone={o.net < 0 ? 'bad' : 'good'}
                  l={t('pricing.padNetProfit')} r={money0(o.net)}
                  fx={hint(t('pricing.padNetProfit'), t('pricing.fxNetRule'),
                    [`${money2(o.gross)} − ${money2(o.oh)}`])} />
                <Line l={t('pricing.padNetMargin')}
                  r={o.margin === null ? '—' : `${(o.margin * 100).toFixed(1)}%`}
                  fx={hint(t('pricing.padNetMargin'), t('pricing.fxMarginRule'),
                    [`${money2(o.net)} ÷ ${money2(o.rev)}`])} />
              </>
            );
          })()}
          {foot ? foot(o) : null}
        </div>
      ))}
    </div>
  );
};

/* THE QUOTE'S OTHER COSTINGS, AS CARDS — Max, 2026-10-01: "cho 2 option kia như card A đi nè rùi
   soạn email báo lại Jay để xem ảnh thấy như thế nào."

   They were a four-column table for half an hour and it read as a footnote. As cards they read as
   what they are: the same quote costed another way, next to the three ways the extra can be split.

   PRICED AT A, AND THE CARD SAYS SO. Every figure below the terms box — billed, absorbed, gross,
   O.H, net, margin — depends on who carries the extra, and that is the decision ABOVE and not
   taken yet. Pricing them at A is what makes them comparable with card A, which is what "như card
   A" asks for, and it is the split the quote itself is written on. A card silently priced at one
   split while sitting beside three others would be a figure with no stated basis.

   ORDER-LEVEL, like A, B and C: the same `cardTotals` fold across every SKU, applying the option
   to each SKU that carries one at that index. Per SKU they would be a different scale in the same
   visual language, which is how two numbers on one screen come to be read as comparable when they
   are not.

   THEY CAN BE CHOSEN — Max, 2026-10-01, reversing himself a minute after "ko cho bấm chọn được
   luôn nha" with "cho bấm chọn được luôn nha". The later word stands.
   CHOOSING ONE MEANS "this costing, with the partner carrying the extra", which is what the card
   says on its face and the only reading the figures on it support. The stored key is `O1`..`O99`
   and carries that split by definition — there is no second axis and no lever on the option; the
   day there is one, this comment is where it starts.
   **This is a departure from Jay's written instruction** — "They just don't appear on Pricing
   Approval" — taken knowingly, and the mail to him says so and shows him the screen. */
export const cpOptionCards = (skus, rules, t, items) => {
  const list = skus || [];
  const n = list.reduce((a, x) => Math.max(a, (x.options || []).length), 0);
  return Array.from({ length: n }, (_, i) => {
    const owner = list.find((sk) => (sk.options || [])[i]);
    const opt = owner ? owner.options[i] : null;
    const tot = cardTotals(list, rules,
      (sk) => ((sk.options || [])[i] ? withOption(sk, sk.options[i]) : sk));
    return {
      key: `O${i + 1}`,
      opt: true,
      name: (opt && String(opt.name || '').trim()) || t('pricing.padOptionN', { n: i + 1 }),
      num: t('pricing.padOptionN', { n: i + 1 }),
      diff: (opt && optDepartureText(owner.values || {}, opt, t, items || {}))
        || t('pricing.optSameShort'),
      /* The split, stated on the card rather than in a note under the row: this card can be
         chosen, so a reader has to be able to tell from the card alone what choosing it means. */
      whoHead: t('pricing.padRefAt'),
      who: t('pricing.padWhoA'),
      ...priceCard(tot, 'A'),
    };
  });
};


/* ---------------------------------------------------------------------------------------------
   BLOCK 7 ON THE CBD METHOD — who carries the two surcharges, which is the decision Linda's workbook
   leaves open (Calculation B10, B11: "HPAC absorbs Production MOQ Surcharge / Short Lead-Time
   Surcharge"). Its four answers are the four cards:

     A   the partner pays both — the workbook's default, No / No
     B   HPAC absorbs both
     HM  HPAC absorbs the MOQ surcharge only
     HL  HPAC absorbs the short-lead surcharge only

   then the quote's own costings (priced at A, the same rule as on the guideline) and the terms card.
   Every card is the order priced with those two ticks set — `computeSku` again, never a second
   reading of it — so a card's GP is exactly what the workbook would print with those two cells set.
   Stored keys 'A', 'B', 'HM', 'HL'; the letter is still the place in the row. */
export const CBD_CARDS = [
  { key: 'A', moq: false, sl: false },
  { key: 'B', moq: true, sl: true },
  { key: 'HM', moq: true, sl: false },
  { key: 'HL', moq: false, sl: true },
];

const cbdTotals = (skus, rules, mutate) => skus.reduce((a, sk0) => {
  const sk = mutate ? mutate(sk0) : sk0;
  if (!sk) return a;
  const c = computeSku(sk, rules);
  const k = c.cbd;
  if (!k) return a;
  const q = n2(c.qty);
  const absorbed = (k.absorbMoq ? k.moq : 0) + (k.absorbSl ? k.sl : 0);
  return {
    qty: a.qty + q,
    rev: a.rev + k.t2 * q,
    t1: a.t1 + k.t1 * q,
    cost: a.cost + k.factory * q,
    absorbed: a.absorbed + absorbed * q,
    extra: a.extra + (k.moq + k.sl) * q,
    retail: a.retail + n2(c.msrp) * q,
    retailParts: [...a.retailParts, [q, n2(c.msrp), String(sk.name || '').trim()]],
    unit: a.unit === null ? k.t2 : (a.unit === k.t2 ? a.unit : NaN),
  };
}, { qty: 0, rev: 0, t1: 0, cost: 0, absorbed: 0, extra: 0, retail: 0, retailParts: [], unit: null });

const withAbsorb = (sk, card) => ({ ...sk, values: { ...sk.values, absorbMoq: card.moq, absorbSl: card.sl } });

export const cpCbdCards = (skus, rules, t, items) => {
  const list = skus || [];
  const priced = (tot) => {
    const gp = tot.rev - tot.cost;
    return { ...tot, gp, margin: tot.rev ? gp / tot.rev : null };
  };
  const cards = [
    ...CBD_CARDS.map((cd) => ({
      key: cd.key, cbd: true, name: t(`pricing.cbdCard_${cd.key}`), who: t(`pricing.cbdCardWho_${cd.key}`),
      src: 'portal', srcLabel: t('pricing.padSrcPortal'),
      ...priced(cbdTotals(list, rules, (sk) => withAbsorb(sk, cd))),
    })),
  ];
  const n = list.reduce((a, x) => Math.max(a, (x.options || []).length), 0);
  for (let i = 0; i < n; i += 1) {
    const owner = list.find((sk) => (sk.options || [])[i]);
    const opt = owner ? owner.options[i] : null;
    const tot = cbdTotals(list, rules, (sk) => withAbsorb(
      (sk.options || [])[i] ? withOption(sk, sk.options[i]) : sk, CBD_CARDS[0]));
    cards.push({
      key: `O${i + 1}`, cbd: true, opt: true,
      name: (opt && String(opt.name || '').trim()) || t('pricing.padOptionN', { n: i + 1 }),
      diff: (opt && optDepartureText(owner.values || {}, opt, t, items || {})) || t('pricing.optSameShort'),
      whoHead: t('pricing.padRefAt'), who: t('pricing.cbdCardWho_A'),
      src: 'quote', srcLabel: t('pricing.padSrcQuote'),
      ...priced(tot),
    });
  }
  cards.push({
    key: 'TERMS', name: t('pricing.padOptTerms'), who: t('pricing.padOptTermsSub'), hand: true,
    src: 'approver', srcLabel: t('pricing.padSrcApprover'),
  });
  cards.forEach((c, i) => { c.letter = String.fromCharCode(65 + i); });
  const ord = cbdTotals(list, rules, (sk) => withAbsorb(sk, CBD_CARDS[0]));
  return { cards, cols: Math.min(cards.length, 3), ord, method: 'cbd' };
};

/* The figures on a CBD card, each with the working it came from — the same ⓘ the guideline cards
   carry, reading the card's own stored numbers. */
const CbdCardLines = ({ o }) => {
  const { t } = useTranslation();
  const m2 = (x) => n2(x).toFixed(2);
  const hint = (title, rule, work) => <FormulaHint title={title} rule={rule} work={work} result="" align="right" />;
  return (
    <>
      {Number.isFinite(o.unit) && <Line l={t('pricing.cbdT2Unit')} r={money(o.unit)} />}
      {o.retail > 0 && (
        <Line l={t('pricing.padRevenue')} r={money0(o.retail)}
          fx={hint(t('pricing.padRevenue'), t('pricing.fxRevenueRule'),
            [(o.retailParts || []).map(([q, m]) => `${n2(q)} × ${m2(m)}`).join(' + ')])} />
      )}
      <Line l={t('pricing.cbdOrderValue')} r={money0(o.rev)}
        fx={hint(t('pricing.cbdOrderValue'), t('pricing.cbdOrderValueRule'), [`Σ T2 × ${t('pricing.qty')}`])} />
      <Line l={t('pricing.cbdFactoryAmt')} r={`−${money0(o.cost).replace('−', '')}`}
        fx={hint(t('pricing.cbdFactoryAmt'), t('pricing.cbdFactoryAmtRule'), [`Σ ${t('pricing.cbdFactory')} × ${t('pricing.qty')}`])} />
      <Line dim l={t('pricing.cbdAbsorbedAmt')} r={money0(o.absorbed)}
        fx={hint(t('pricing.cbdAbsorbedAmt'), t('pricing.cbdAbsorbedRule'), [m2(o.absorbed)])} />
      <Line hero tone={o.gp < 0 ? 'bad' : 'good'} l={t('pricing.cbdGp')} r={money0(o.gp)}
        fx={hint(t('pricing.cbdGp'), t('pricing.cbdGpRule'), [`${m2(o.rev)} − ${m2(o.cost)}`])} />
      <Line l={t('pricing.cbdMargin')} r={o.margin === null ? '—' : `${(o.margin * 100).toFixed(1)}%`}
        fx={hint(t('pricing.cbdMargin'), t('pricing.cbdMarginRule'), [`${m2(o.gp)} ÷ ${m2(o.rev)}`])} />
    </>
  );
};

/* THE CBD QUOTE AS ONE DECISION PRICES IT — the chosen card's absorb ticks (or an option's costing at
   A) applied to every SKU, then priced. Shared by the key points and the Partner Offer, so the offer
   the partner receives is exactly the card that was approved. Null for the terms card and for no
   decision: neither is a price the portal can work out. */
export const cbdPricedBy = (skus, rules, key) => {
  const list = skus || [];
  const fixed = CBD_CARDS.find((c) => c.key === (key || 'A'));
  const optIx = /^O(\d+)$/.test(String(key || '')) ? Number(String(key).slice(1)) - 1 : -1;
  /* YL's own Gross Margin % ('GM30', the Price Approval template's last option; 'GM30/25' one per item since 29/10): each
     SKU at T2 = vendor cost ÷ (1 − its margin), rounded up — computeCbd's cbdMargin. */
  const gms = gmsOf(key, list.length);
  if (!fixed && optIx < 0 && !gms) return null;
  return list.map((sk, i) => {
    const base = gms ? { ...sk, values: { ...sk.values, cbdMargin: gms[i] } }
      : (optIx >= 0 && (sk.options || [])[optIx] ? withOption(sk, sk.options[optIx]) : sk);
    const priced = withAbsorb(base, fixed || CBD_CARDS[0]);
    return { sku: priced, c: computeSku(priced, rules) };
  });
};

/* "KEY POINTS FOR DECISION" — the workbook's CEO Approval sheet, B20:B24, as sentences worked out
   from the same figures as the cards: the weighted margin on the order at T2, the style with the
   lowest margin, how the vendor cost is made, the lead time and what HPAC absorbs. On the card the approver is
   looking at — the one chosen, or A until one is. */
export const CpCbdKeyPoints = ({ skus, rules, decided }) => {
  const { t } = useTranslation();
  const key = decided && decided !== 'TERMS' ? decided : 'A';
  const rows = cbdPricedBy(skus, rules, key);
  if (!rows || !rows.length) return null;
  const tot = rows.reduce((a, { c }) => ({
    rev: a.rev + c.cbd.t2 * n2(c.qty), cost: a.cost + c.cbd.factory * n2(c.qty),
  }), { rev: 0, cost: 0 });
  const gp = tot.rev - tot.cost;
  const low = rows.reduce((m, r, i) => (r.c.cbd.margin !== null && (m === null || r.c.cbd.margin < m.margin)
    ? { margin: r.c.cbd.margin, name: String(r.sku.name || '').trim() || t('pricing.padSkuN', { n: i + 1 }) } : m), null);
  const first = (rows[0].sku.values || {});
  const lead = rows[0].c.lead;
  const k0 = rows[0].c.cbd;
  const day = (d) => {
    if (!d) return '—';
    const x = new Date(`${d}T00:00:00`);
    return Number.isNaN(x.getTime()) ? d : x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '/');
  };
  const absorbed = [k0.absorbMoq && t('pricing.cbdMoq'), k0.absorbSl && t('pricing.cbdSl')].filter(Boolean);
  /* How the price sits against the inline product — Tina's first key point (2026-10-23). */
  const shares = rows.map(({ sku, c }) => {
    const r = n2((sku.values || {}).inlineRrp);
    return r > 0 && c.cbd.t2 ? (c.cbd.t2 / r) * 100 : null;
  });
  const known = shares.filter((x) => x !== null);
  const lo = known.length ? Math.min(...known) : null;
  const hi = known.length ? Math.max(...known) : null;
  const inl = lo === null ? null : t('pricing.cbdKpInline', {
    pct: lo.toFixed(0) === hi.toFixed(0) ? lo.toFixed(0) : `${lo.toFixed(0)}–${hi.toFixed(0)}`,
  }) + (known.length < rows.length ? ` ${t('pricing.cbdKpInlineSome', { n: rows.length - known.length })}` : '');
  return (
    <div className="pad-keypts">
      <b>{t('pricing.cbdKeyPoints')}</b>
      <ul>
        <li>{t('pricing.cbdKpMargin', { pct: tot.rev ? ((gp / tot.rev) * 100).toFixed(1) : '—', rev: money0(tot.rev), gp: money0(gp) })}</li>
        {low && rows.length > 1 && <li>{t('pricing.cbdKpLowest', { name: low.name, pct: (low.margin * 100).toFixed(1) })}</li>}
        {inl && <li>{inl}</li>}
        <li>{t('pricing.cbdKpT0')}</li>
        {lead !== null && <li>{t('pricing.cbdKpLead', { days: lead, po: day(first.poDate), xf: day(first.xfDate) })}</li>}
        <li>{absorbed.length ? t('pricing.cbdKpAbsorbs', { what: absorbed.join(' + ') }) : t('pricing.cbdKpAbsorbsNone')}</li>
      </ul>
    </div>
  );
};

/* PRICE, PROFIT AND MARGIN, AND A MARGIN TO TRY — Tina Lee, 2026-10-23, on Linda's CBD file: "YL
   prefers a simple and straightforward format that clearly shows the selling price, expected profit,
   and margin rate. YL should be able to adjust the margin rate directly and immediately see how the
   resulting profit changes." Max chose the margin ON THE SELLING PRICE (T2), 2026-10-26:
     selling price = vendor cost ÷ (1 − margin), rounded UP to the dollar as Linda's T2 is,
   so the margin printed beside the tried price is the margin that price really makes.
   ONE TABLE FOR THE WHOLE QUOTE, above the per-SKU blocks: the decision is one per quote, and a
   number per SKU tab is exactly the hunt Tina is asking to remove.
   A TRY CHANGES NOTHING ON THE QUOTE. The approval panel shows figures frozen at submission (Jay's
   rule); a tried margin is a scenario, and the approver who wants it takes it through the terms card
   — `onUse` puts the tried prices into the instruction box, which is what that card records. */
const upDollar = (x) => Math.ceil(Number(x) - 1e-9);
export const CbdDecisionStrip = ({ skus, rules, decided, cardLabel, onUse }) => {
  const { t } = useTranslation();
  const [tryPct, setTryPct] = React.useState('');
  const key = decided && decided !== 'TERMS' && cbdPricedBy(skus, rules, decided) ? decided : 'A';
  const rows = cbdPricedBy(skus, rules, key);
  if (!rows || !rows.length || !rows.every((r) => r.c && r.c.cbd)) return null;
  const pctNum = Number(String(tryPct).replace(',', '.'));
  const m = String(tryPct).trim() !== '' && Number.isFinite(pctNum) && pctNum > 0 && pctNum < 95 ? pctNum / 100 : null;
  const lines = rows.map(({ sku, c }, i) => {
    const qty = n2(c.qty);
    const cost = c.cbd.factory;
    const price = c.cbd.t2;
    const at = m === null ? null : upDollar(cost / (1 - m));
    return {
      name: String(sku.name || '').trim() || t('pricing.padSkuN', { n: i + 1 }),
      qty, cost, price, profit: price - cost, margin: price ? (price - cost) / price : null,
      inline: n2((sku.values || {}).inlineRrp),
      at, atProfit: at === null ? null : at - cost, atMargin: at ? (at - cost) / at : null,
    };
  });
  const sum = (f) => lines.reduce((a, l) => a + f(l), 0);
  const tot = {
    qty: sum((l) => l.qty), cost: sum((l) => l.cost * l.qty), rev: sum((l) => l.price * l.qty),
    atRev: m === null ? null : sum((l) => l.at * l.qty),
  };
  tot.profit = tot.rev - tot.cost;
  tot.margin = tot.rev ? tot.profit / tot.rev : null;
  if (tot.atRev !== null) { tot.atProfit = tot.atRev - tot.cost; tot.atMargin = tot.atRev ? tot.atProfit / tot.atRev : null; }
  /* Max, 2026-10-26: Tina's "compared with our inline products" is the INLINE RETAIL price. It is
     required on a CBD quote now, so the column is always drawn; the ratio says what share of the
     inline shelf price the partner pays. */
  const anyInline = true;
  const vsInl = (price, l) => (l.inline > 0 && price ? price / l.inline : null);
  const pc = (x) => (x === null || !Number.isFinite(x) ? '—' : `${(x * 100).toFixed(1)}%`);
  const shown = m === null ? '' : String(Math.round(m * 1000) / 10);
  const use = () => {
    if (!onUse || m === null) return;
    onUse(t('pricing.cbdStripInstr', {
      pct: shown,
      list: lines.map((l) => `${l.name} ${money(l.at)} × ${l.qty}`).join('; '),
      rev: money0(tot.atRev), gp: money0(tot.atProfit),
    }));
  };
  const scen = m !== null;
  return (
    <div className="pad-block cbd-strip">
      <div className="pad-btitle">
        <h4>{t('pricing.cbdStripTitle')}</h4>
        <span className="pad-kind">{cardLabel ? t('pricing.cbdStripAsCard', { card: cardLabel }) : t('pricing.cbdStripWhole')}</span>
      </div>
      <div className="cbd-strip-try">
        <label>
          <span>{t('pricing.cbdStripTry')}</span>
          <input type="number" min="1" max="94" step="0.5" value={tryPct} placeholder={pc(tot.margin).replace('%', '')}
            onChange={(e) => setTryPct(e.target.value)} aria-label={t('pricing.cbdStripTry')} />
          <b>%</b>
        </label>
        {[25, 30, 35].map((x) => (
          <button key={x} type="button" className={`cbd-chip${shown === String(x) ? ' on' : ''}`} onClick={() => setTryPct(String(x))}>{x}%</button>
        ))}
        {scen && <button type="button" className="cbd-chip clear" onClick={() => setTryPct('')}>{t('pricing.cbdStripClear')}</button>}
        <em>{t('pricing.cbdStripTryHint')}</em>
      </div>
      <div className="cbd-strip-wrap">
        <table className="cbd-strip-tt">
          <thead>
            <tr>
              <th rowSpan={2}>{t('pricing.skuName')}</th>
              <th rowSpan={2} className="n">{t('pricing.cbdStripQty')}</th>
              <th rowSpan={2} className="n">{t('pricing.cbdFactory')}</th>
              {anyInline && <th rowSpan={2} className="n">{t('pricing.cbdStripInline')}</th>}
              <th colSpan={4} className="grp">{t('pricing.cbdStripNow')}</th>
              {scen && <th colSpan={4} className="grp try">{t('pricing.cbdStripAt', { pct: shown })}</th>}
            </tr>
            <tr>
              <th className="n">{t('pricing.cbdStripPrice')}</th><th className="n">{t('pricing.cbdStripVsInline')}</th><th className="n">{t('pricing.cbdStripProfit')}</th><th className="n">{t('pricing.cbdStripMargin')}</th>
              {scen && <><th className="n try">{t('pricing.cbdStripPrice')}</th><th className="n try">{t('pricing.cbdStripVsInline')}</th><th className="n try">{t('pricing.cbdStripProfit')}</th><th className="n try">{t('pricing.cbdStripMargin')}</th></>}
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td data-val>{l.name}</td>
                <td className="n">{l.qty}</td>
                <td className="n">{money(l.cost)}</td>
                {anyInline && <td className="n">{l.inline > 0 ? money(l.inline) : '—'}</td>}
                <td className="n b">{money(l.price)}</td>
                <td className="n">{pc(vsInl(l.price, l))}</td>
                <td className="n">{money(l.profit)}</td>
                <td className="n">{pc(l.margin)}</td>
                {scen && <><td className="n b try">{money(l.at)}</td><td className="n try">{pc(vsInl(l.at, l))}</td><td className="n try">{money(l.atProfit)}</td><td className="n try">{pc(l.atMargin)}</td></>}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>{t('pricing.cbdStripTotal')}</td>
              <td className="n">{tot.qty}</td>
              <td className="n">{money0(tot.cost)}</td>
              {anyInline && <td />}
              <td className="n b">{money0(tot.rev)}</td>
              <td />
              <td className="n b">{money0(tot.profit)}</td>
              <td className="n b">{pc(tot.margin)}</td>
              {scen && <><td className="n b try">{money0(tot.atRev)}</td><td className="try" /><td className="n b try">{money0(tot.atProfit)}</td><td className="n b try">{pc(tot.atMargin)}</td></>}
            </tr>
          </tfoot>
        </table>
      </div>
      {scen && onUse && (
        <div className="cbd-strip-use">
          <button type="button" className="sp-btn" onClick={use}>{t('pricing.cbdStripUse')}</button>
          <em>{t('pricing.cbdStripUseHint')}</em>
        </div>
      )}
    </div>
  );
};

/* THE PRICE APPROVAL TEMPLATE — Linda Pham's "27SS Venice MArt x HX Price Approval_20261026.xlsx", approved by Tina
   to be what Young decides on (Max, 2026-10-26: "làm nhé"). It answers Tina's three questions on one sheet: the price
   against the inline product, the profit at that price, and what another margin would do.
     1. Pricing options vs inline: Option 1 = the partner pays the surcharges (card A), Option 2 = HPAC absorbs them
        (card B), any costing Sales/Costing added (priced at A), and the LAST option is YL's own Gross Margin % —
        T2 = ROUNDUP(Buying cost ÷ (1 − GM%)). Per SKU: buying cost (incl. both surcharges), HPAC overhead, T1 (always
        Option 1's, as the template keeps H15 down every option), inline T1, T1 vs inline T1, inline MSRP, the partner's
        MSRP (inline × (1 + uplift), reference), T2, GP/pc, GM% on T2, Net margin % after the HPAC overhead, totals,
        T2 % of the partner MSRP and "T2 ≥ T1".
     2. Margin sensitivity 15–45%.
     3. Approval: the option ticked, its T2 per SKU and totals.
   Stored keys: 'A', 'B', 'O1'.., and 'GM30' for YL's margin (one margin for every SKU of the quote). */
export const CBD_SENS = [15, 20, 25, 30, 35, 40, 45];
const ceil0 = (x) => Math.ceil(Number(x) - 1e-9);
/* YL's margin as a decision key: 'GM30' when every item takes the same, 'GM30/25' one per item in the quote's order
   (Linda, 29/10: "nếu YL muốn chọn margin từng cái thì thay vì chọn nguyên project"). */
export const gmKey = (pct) => {
  const list = [].concat(pct).map((x) => Math.round(Number(x) * 10) / 10);
  return list.every((x) => x === list[0]) ? `GM${list[0]}` : `GM${list.join('/')}`;
};
export const gmOf = (key) => (/^GM\d+(\.\d)?$/.test(String(key || '')) ? Number(String(key).slice(2)) : null);
export const isGmKey = (key) => /^GM\d/.test(String(key || ''));
/* the margins of a key, one per item (n items), or null — 'GM30' is 30 for each */
export const gmsOf = (key, n) => {
  const m = /^GM(\d+(?:\.\d)?(?:\/\d+(?:\.\d)?)*)$/.exec(String(key || ''));
  if (!m) return null;
  const xs = m[1].split('/').map(Number);
  if (xs.length === 1) return Array.from({ length: n }, () => xs[0]);
  return xs.length === n ? xs : null;
};
const gmLabel = (list) => (!list || !list.length ? '' : list.every((x) => x === list[0]) ? `${list[0]}%` : list.map((x) => `${x}%`).join(' / '));
export const cbdApprovalModel = (skus, rules, gmPct, decided = '') => {
  const list = (skus || []).filter((sk) => sk && isCbd(sk.values));
  if (!list.length) return null;
  const base = list.map((sk, i) => {
    const a = computeSku(withAbsorb(sk, CBD_CARDS[0]), rules);
    const v = sk.values || {};
    const inlineMsrp = n2(v.inlineRrp);
    const uplift = v.premium === '' || v.premium === undefined ? 0 : n2(v.premium);
    return {
      sk, i, name: String(sk.name || '').trim(), color: String(v.color || '').trim(), styleCode: String(v.styleCode || '').trim(),
      qty: n2(a.qty), cost: a.cbd.factory, oh: a.cbd.oh, t1: a.cbd.t1, t2A: a.cbd.t2, surcharge: n2(a.cbd.moq) + n2(a.cbd.sl),
      inlineT1: n2(v.inlineT1), inlineMsrp, uplift,
      /* to the cent, as Excel shows 142.945 → 142.95 (binary 142.94499… would print .94) */
      partnerMsrp: inlineMsrp ? Math.round((inlineMsrp * (1 + uplift / 100) + 1e-9) * 100) / 100 : 0,
    };
  });
  const row = (b, t2, gmIn) => {
    const gp = t2 === null ? null : t2 - b.cost;
    return {
      ...b, t2, gmIn: gmIn ?? null, gp, gm: t2 ? gp / t2 : null, net: t2 ? (gp - b.oh) / t2 : null,
      sales: t2 === null ? null : t2 * b.qty, gpTot: gp === null ? null : gp * b.qty,
      ofMsrp: t2 && b.partnerMsrp ? t2 / b.partnerMsrp : null, below: t2 !== null && t2 < b.t1,
    };
  };
  const total = (rows) => {
    if (rows.some((r) => r.t2 === null)) return null;
    const qty = rows.reduce((s, r) => s + r.qty, 0);
    const sales = rows.reduce((s, r) => s + r.sales, 0);
    const gp = rows.reduce((s, r) => s + r.gpTot, 0);
    const oh = rows.reduce((s, r) => s + r.oh * r.qty, 0);
    return { qty, sales, gp, gm: sales ? gp / sales : null, net: sales ? (gp - oh) / sales : null };
  };
  const opts = [];
  opts.push({ key: 'A', kind: 'A', rows: base.map((b) => row(b, b.t2A)) });
  /* "HPAC absorbs the surcharges" only when there is a surcharge to absorb (Max, 05/11/2026: no MOQ and no short-lead surcharge on
     any item → no Option 2; it would only repeat Option 1). Kept when YL already chose it, so a decision never loses its row. */
  if (base.some((b) => b.surcharge > 0) || String(decided || '').toUpperCase() === 'B') {
    opts.push({ key: 'B', kind: 'B', rows: base.map((b) => row(b, computeSku(withAbsorb(b.sk, CBD_CARDS[1]), rules).cbd.t2)) });
  }
  const n = list.reduce((m, x) => Math.max(m, (x.options || []).length), 0);
  for (let k = 0; k < n; k += 1) {
    const owner = list.find((x) => (x.options || [])[k]);
    opts.push({
      key: `O${k + 1}`, kind: 'O', name: (owner && String(owner.options[k].name || '').trim()) || null,
      rows: base.map((b) => {
        const o = (b.sk.options || [])[k];
        return row(b, computeSku(withAbsorb(o ? withOption(b.sk, o) : b.sk, CBD_CARDS[0]), rules).cbd.t2);
      }),
    });
  }
  // YL's margin: one for every item, or one per item (an array in the items' order — Linda, 29/10)
  const gl = base.map((b, i) => Number(Array.isArray(gmPct) ? gmPct[i] : gmPct));
  const okOne = (x) => Number.isFinite(x) && x > 0 && x < 95;
  const gmOk = gl.every(okOne);
  opts.push({ key: gmOk ? gmKey(gl) : null, kind: 'GM', gm: gmOk ? gl : null,
    rows: base.map((b, i) => row(b, okOne(gl[i]) ? ceil0(b.cost / (1 - gl[i] / 100)) : null, okOne(gl[i]) ? gl[i] / 100 : null)) });
  opts.forEach((o, i) => { o.no = i + 1; o.total = total(o.rows); });
  const oh = base.reduce((s, b) => s + b.oh * b.qty, 0);
  const sens = CBD_SENS.map((pct) => {
    const per = base.map((b) => { const t2 = ceil0(b.cost / (1 - pct / 100)); return { t2, gp: t2 - b.cost }; });
    const sales = per.reduce((s, x, i) => s + x.t2 * base[i].qty, 0);
    const gp = per.reduce((s, x, i) => s + x.gp * base[i].qty, 0);
    return { pct, per, sales, gp, net: sales ? (gp - oh) / sales : null };
  });
  return { base, opts, sens };
};

const pctTxt = (x, d = 1) => (x === null || x === undefined || !Number.isFinite(x) ? '—' : `${(x * 100).toFixed(d)}%`);
/* THE OPTION-3 MARGINS THE QUOTE PROPOSES (Max, 29/10: "2 số này lấy từ lúc upload lên và người tạo có thể sửa và lưu") —
   `paGm` on each CBD item, read from the Price Approval workbook on import and kept with the quote on Save. YL's own
   decision (extra_option) still wins wherever there is one; 30 only when neither says anything. */
export const proposedGms = (skus) => (skus || []).filter((sk) => sk && isCbd(sk.values)).map((sk) => {
  // as typed — an emptied box stays empty while somebody types; 30 only where nothing was ever set
  const v = sk.values.paGm;
  return v === undefined || v === null ? '30' : String(v);
});
const gmNum = (x) => { const n = Number(x); return n >= 0.5 && n <= 94 ? n : 30; };
export const CbdPriceApproval = ({ skus, rules, decided, canDecide, busy, onPick, foot, record, onGmChange }) => {
  const { t } = useTranslation();
  const nItems = (skus || []).filter((sk) => sk && isCbd(sk.values)).length;
  const decidedGms = gmsOf(decided, nItems);
  const decidedKey = decidedGms ? decidedGms.join('/') : '';
  const proposal = proposedGms(skus);
  const proposalKey = proposal.join('/');
  /* ONE BOX PER ITEM (Linda, 29/10) — a quote of several items, each its own quantity, may take a margin each */
  const [gms, setGms] = React.useState(() => (decidedGms ? decidedGms.map(String) : proposal));
  const setGmAt = (i, v) => {
    setGms((a) => Array.from({ length: nItems }, (_, k) => (k === i ? v : (a[k] ?? '30'))));
    if (onGmChange) onGmChange(i, v);
  };
  // a quote opened, imported or switched brings its own proposal with it — unless YL has decided
  React.useEffect(() => { if (!decidedKey) setGms(proposalKey.split('/').filter((x) => x !== '')); }, [proposalKey, decidedKey]);
  /* THE FOUR INLINE COLUMNS AS AN EXCEL GROUP, folded when the sheet opens (Max, 28/10: "group 4 cột này và mặc định lúc
     đầu là group") — the comparison is there one click away, and the decision columns get the room */
  const [inlineOpen, setInlineOpen] = React.useState(false);
  React.useEffect(() => { if (decidedKey) setGms(decidedKey.split('/').map(String)); }, [decidedKey]);
  React.useEffect(() => { setGms((a) => (a.length === nItems ? a : Array.from({ length: nItems }, (_, k) => a[k] ?? a[0] ?? '30'))); }, [nItems]);
  const model = cbdApprovalModel(skus, rules, gms, decided);
  const sameGm = gms.length && gms.every((x) => String(x) === String(gms[0])) ? String(gms[0]) : null;
  if (!model) return null;
  const { base, opts, sens } = model;
  const nameOf = (o) => {
    if (o.kind === 'A') return t('pricing.paOpt1');
    if (o.kind === 'B') return t('pricing.paOpt2');
    if (o.kind === 'GM') return t('pricing.paOptGm');
    return o.name || t('pricing.padOptionN', { n: o.key.slice(1) });
  };
  const subOf = (o) => (o.kind === 'A' ? t('pricing.paOpt1Sub') : o.kind === 'B' ? t('pricing.paOpt2Sub')
    : o.kind === 'GM' ? t('pricing.paOptGmSub') : t('pricing.paOptOSub'));
  const isChosen = (o) => o.key && decided === o.key;
  const chosen = opts.find((o) => o.key && o.key === decided)
    || (decidedGms ? { ...opts[opts.length - 1] } : null);
  const anyInlineT1 = base.some((b) => b.inlineT1 > 0);
  const gmOpt = opts.find((o) => o.kind === 'GM');
  const partner = String(((skus.find((x) => isCbd(x && x.values)) || {}).values || {}).partnerName || '').trim();
  const m2 = (x) => (x === null || x === undefined ? '—' : money(x));
  return (
    <div className="pad-block cbd-pa">
      <div className="pad-btitle">
        <h4>{t('pricing.paS1')}</h4>
        <span className="pad-kind">{t('pricing.paS1Kind')}</span>
      </div>
      {/* not one of the sheet's notes (Linda checked them against hers, 28/10) — a missing input, so said apart */}
      {!anyInlineT1 && <p className="cbd-pa-warn">{t('pricing.paNoInlineT1')}</p>}
      <div className="cbd-pa-wrap">
        <table className="cbd-pa-tt">
          <thead>
            <tr>
              <th className="opt">{t('pricing.paOption')}</th>
              <th className="nm">{t('pricing.paItem')}</th>
              <th>{t('pricing.paStyle')}</th>
              <th className="n">{t('pricing.paQty')}</th>
              <th className="n">{t('pricing.paCost')}</th>
              <th className="n">{t('pricing.paOh')}</th>
              <th className="n">{t('pricing.paT1')}</th>
              {inlineOpen ? (
                <>
                  <th className="n grp-first">
                    <button type="button" className="cbd-pa-grp" onClick={() => setInlineOpen(false)}
                      title={t('pricing.paGrpHide')} aria-label={t('pricing.paGrpHide')} data-cbd-grp="open">−</button>
                    {t('pricing.paInlineT1')}
                  </th>
                  <th className="n grp">{t('pricing.paT1vs')}</th>
                  <th className="n grp">{t('pricing.paInlineMsrp')}</th>
                  <th className="n grp grp-last">{t('pricing.paPartnerMsrp')}</th>
                </>
              ) : (
                <th className="grp-shut">
                  <button type="button" className="cbd-pa-grp" onClick={() => setInlineOpen(true)}
                    title={t('pricing.paGrpShow')} aria-label={t('pricing.paGrpShow')} data-cbd-grp="shut">+</button>
                </th>
              )}
              <th className="n gm">{t('pricing.paGmIn')}</th>
              <th className="n t2">{t('pricing.paT2')}</th>
              <th className="n">{t('pricing.paGp')}</th>
              <th className="n">{t('pricing.paGm')}</th>
              <th className="n">{t('pricing.paNet')}</th>
              <th className="n">{t('pricing.paSales')}</th>
              <th className="n">{t('pricing.paGpTot')}</th>
              <th className="n">{t('pricing.paOfMsrp')}</th>
              <th>{t('pricing.paCheck')}</th>
              {/* ONE option is approved (Linda, 27/10: "chỉ chọn 1 thôi") — said in the heading, not left to the notes */}
              <th className="ap">{t('pricing.paApprove')}<small className="cbd-pa-one">{t('pricing.paApproveOne')}</small></th>
            </tr>
          </thead>
          {opts.map((o) => (
            <tbody key={o.no} className={`${isChosen(o) ? 'on' : ''} k-${o.kind}`}>
              {o.rows.map((r, j) => (
                <tr key={j}>
                  {j === 0 && (
                    <td className="opt" rowSpan={o.rows.length + 1}>
                      <b>{t('pricing.paOptionN', { n: o.no })} – {nameOf(o)}</b>
                      <small>{subOf(o)}</small>
                    </td>
                  )}
                  <td className="nm">{r.name}{r.color ? ` – ${r.color}` : ''}</td>
                  <td>{r.styleCode || '—'}</td>
                  <td className="n">{n2(r.qty).toLocaleString('en-US')}</td>
                  <td className="n">{money(r.cost)}</td>
                  <td className="n">{money(r.oh)}</td>
                  <td className="n">{money(r.t1)}</td>
                  {inlineOpen ? (
                    <>
                      <td className="n grp-first">{r.inlineT1 ? money(r.inlineT1) : '—'}</td>
                      <td className="n grp">{r.inlineT1 ? pctTxt(r.t1 / r.inlineT1 - 1) : '—'}</td>
                      <td className="n grp">{r.inlineMsrp ? money(r.inlineMsrp) : '—'}</td>
                      <td className="n grp grp-last">{r.partnerMsrp ? money(r.partnerMsrp) : '—'}</td>
                    </>
                  ) : <td className="grp-shut" />}
                  {/* YL's margin PER ITEM (Linda, 29/10: "mỗi item có số lượng khác nhau, nếu YL muốn chọn margin từng cái") —
                      a box on every item's row, nothing else in the cell (Max, same day: the "same for all" button and the
                      "a margin per item" line came off) */}
                  {o.kind !== 'GM' ? <td className="n gm">–</td> : (
                    <td className="n gm gm-each">
                      {canDecide !== undefined ? (
                        <span className="cbd-pa-gm">
                          <input type="number" min="1" max="94" step="0.5" value={gms[j] ?? ''} aria-label={`${t('pricing.paGmIn')} · ${r.name}`}
                            onChange={(e) => setGmAt(j, e.target.value)} data-cbd-gm={j} />%
                        </span>
                      ) : pctTxt(r.gmIn, 0)}
                    </td>
                  )}
                  <td className="n t2"><b>{m2(r.t2)}</b></td>
                  <td className="n">{m2(r.gp)}</td>
                  <td className="n">{pctTxt(r.gm)}</td>
                  <td className={`n shade${r.net !== null && r.net < 0 ? ' neg' : ''}`}>{pctTxt(r.net)}</td>
                  <td className="n">{r.sales === null ? '—' : money0(r.sales)}</td>
                  <td className="n">{r.gpTot === null ? '—' : money0(r.gpTot)}</td>
                  <td className="n">{pctTxt(r.ofMsrp)}</td>
                  <td>{r.t2 === null ? '—' : (r.below ? <span className="cbd-pa-bad">{t('pricing.paBelow')}</span> : <span className="cbd-pa-ok">OK</span>)}</td>
                  {j === 0 && (
                    <td className="ap" rowSpan={o.rows.length + 1}>
                      {isChosen(o) ? <span className="cbd-pa-tick on">✔ {t('pricing.paApproved')}</span>
                        : (canDecide && o.key ? (
                          <button type="button" className={`cbd-pa-approve${chosen ? ' switch' : ''}`} disabled={busy} onClick={() => onPick(o.key)}>
                            {chosen ? t('pricing.paSwitch') : `✔ ${t('pricing.paTick')}`}
                          </button>
                        ) : (!canDecide && !chosen && o.no === 1 ? <span className="cbd-pa-ylonly">{t('pricing.paYlOnly')}</span> : null))}
                    </td>
                  )}
                </tr>
              ))}
              <tr className="tot">
                <td className="nm">{t('pricing.paOptTotal')}</td>
                <td /><td className="n">{o.total ? n2(o.total.qty).toLocaleString('en-US') : ''}</td>
                <td colSpan={inlineOpen ? 10 : 7} />
                <td className="n">{o.total ? pctTxt(o.total.gm) : '—'}</td>
                <td className={`n${o.total && o.total.net < 0 ? ' neg' : ''}`}>{o.total ? pctTxt(o.total.net) : '—'}</td>
                <td className="n">{o.total ? money0(o.total.sales) : '—'}</td>
                <td className="n">{o.total ? money0(o.total.gp) : '—'}</td>
                <td colSpan={2} />
              </tr>
            </tbody>
          ))}
        </table>
      </div>
      {/* THE SHEET'S OWN THREE NOTES, word for word (B24:B26) — Linda, 28/10: "phần nội dung note, em xem lại trên sheet
          của c ghi" — with the quote's own option number, partner and uplift where the template has 3, Venice and 110% */}
      <ul className="cbd-pa-notes">
        <li>{t('pricing.paNote1')}</li>
        <li>{t('pricing.paNote2', { n: gmOpt ? gmOpt.no : opts.length })}</li>
        <li>{t('pricing.paNote3', { partner: partner || t('pricing.paPartnerWord'), pct: 100 + n2(base[0].uplift) })}</li>
      </ul>

      <div className="pad-btitle cbd-pa-h2">
        <h4>{t('pricing.paS2')}</h4>
        <span className="pad-kind">{t('pricing.paS2Kind')}</span>
      </div>
      <div className="cbd-pa-wrap">
        <table className="cbd-pa-tt cbd-pa-sens">
          <thead>
            <tr>
              <th className="n">{t('pricing.paGm')}</th>
              {base.map((b) => <th key={`t${b.i}`} className="n">{b.name}<br />{t('pricing.paT2')}</th>)}
              {base.map((b) => <th key={`g${b.i}`} className="n">{b.name}<br />{t('pricing.paGp')}</th>)}
              <th className="n">{t('pricing.paSales')}</th>
              <th className="n">{t('pricing.paGpTot')}</th>
              <th className="n">{t('pricing.paNetTot')}</th>
            </tr>
          </thead>
          <tbody>
            {sens.map((x) => (
              <tr key={x.pct} className={sameGm !== null && String(x.pct) === sameGm ? 'on' : ''}>
                <td className="n"><b>{x.pct}%</b></td>
                {x.per.map((y, i) => <td key={`t${i}`} className="n">{money(y.t2)}</td>)}
                {x.per.map((y, i) => <td key={`g${i}`} className="n">{money(y.gp)}</td>)}
                <td className="n">{money0(x.sales)}</td>
                <td className="n">{money0(x.gp)}</td>
                <td className={`n${x.net < 0 ? ' neg' : ''}`}>{pctTxt(x.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pad-btitle cbd-pa-h2"><h4>{t('pricing.paS3')}</h4></div>
      <div className="cbd-pa-approval">
        <div><span>{t('pricing.paApprovedOpt')}</span><b>{chosen ? `${t('pricing.paOptionN', { n: chosen.no })} – ${nameOf(chosen)}${chosen.kind === 'GM' && chosen.gm ? ` (${gmLabel(chosen.gm)})` : ''}` : t('pricing.paNoneYet')}</b></div>
        {chosen && chosen.rows.map((r) => <div key={r.i}><span>{t('pricing.paApprovedT2', { name: r.name })}</span><b>{m2(r.t2)}</b></div>)}
        {chosen && chosen.total && (
          <>
            <div><span>{t('pricing.paSales')}</span><b>{money0(chosen.total.sales)}</b></div>
            <div><span>{t('pricing.paGpTot')}</span><b>{money0(chosen.total.gp)}</b></div>
            <div><span>{t('pricing.paGm')}</span><b>{pctTxt(chosen.total.gm)}</b></div>
            <div><span>{t('pricing.paNet')}</span><b>{pctTxt(chosen.total.net)}</b></div>
          </>
        )}
        {record}
      </div>
      {foot}
    </div>
  );
};

/* The approval sheet as the Excel file Linda's template is — laid out by the server with the template's own formulas
   (YL can still type a Gross Margin % into it); the screen sends the figures it worked out. */
export const CbdPriceApprovalExport = ({ projectId, skus, rules, decided, meta, apiBase }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const go = async () => {
    setBusy(true); setErr('');
    try {
      const n = (skus || []).filter((sk) => sk && isCbd(sk.values)).length;
      // YL's decision when there is one, else the margins the quote proposes (paGm)
      const gms = gmsOf(decided, n) || proposedGms(skus).map(gmNum);
      const model = cbdApprovalModel(skus, rules, gms, decided);
      if (!model) throw new Error('No CBD SKU on this quote.');
      const body = {
        decided: decided || '', gm: gms[0] || 30, gms,
        meta: (() => {
          const v = (skus.find((x) => isCbd(x && x.values)) || {}).values || {};
          return { partner: v.partnerName || '', season: v.offerSeason || '', factory: v.factoryName || '',
            priceTerm: v.priceTerm ?? 'FOB HCM, Vietnam', poDate: v.poDate || '', xfDate: v.xfDate || '', ...(meta || {}) };
        })(),
        skus: model.base.map((b) => ({
          name: b.name, color: b.color, styleCode: b.styleCode, qty: b.qty, cost: b.cost, oh: b.oh, t1: b.t1,
          inlineT1: b.inlineT1, inlineMsrp: b.inlineMsrp,
        })),
        uplift: model.base[0].uplift,
        options: model.opts.filter((o) => o.kind !== 'GM').map((o) => ({
          key: o.key, label: o.kind === 'A' ? t('pricing.paOpt1') : o.kind === 'B' ? t('pricing.paOpt2') : (o.name || `Option ${o.key.slice(1)}`),
          sub: o.kind === 'A' ? 'standard formula, surcharges passed through' : o.kind === 'B' ? 'standard formula' : 'costing prepared by Sales/Costing, priced with the partner paying the surcharges',
          t2: o.rows.map((r) => r.t2),
        })),
      };
      const r = await fetch(`${apiBase}/projects/${encodeURIComponent(projectId || 'draft')}/price-approval-xlsx`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) { let m = `HTTP ${r.status}`; try { m = (await r.json()).error || m; } catch (e) { /* keep */ } throw new Error(m); }
      const blob = await r.blob();
      const name = (r.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'Price Approval.xlsx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  if (!skus || !skus.some((sk) => isCbd(sk && sk.values))) return null;
  return (
    <span className="cbd-offer-btn">
      <button type="button" className="sp-btn" disabled={busy} onClick={go} title={t('pricing.paExportHint')}>{t('pricing.paExport')}</button>
      {!!err && <em className="err">{err}</em>}
    </span>
  );
};

/* THE PARTNER OFFER'S ROWS — the workbook's last sheet: item, colour, quantity, the T2 unit price and
   the amount, and nothing else ("no cost data"). Released only once the quote is approved, which the
   server checks again when it lays the file out. */
export const cbdOfferRows = (skus, rules, decided) => {
  const rows = cbdPricedBy(skus, rules, decided);
  if (!rows) return null;
  return rows.map(({ sku, c }, i) => ({
    no: i + 1,
    name: String(sku.name || '').trim() || `SKU ${i + 1}`,
    color: String((sku.values || {}).color || '').trim(),
    qty: n2(c.qty),
    unit: c.cbd.t2,
    amount: c.cbd.t2 * n2(c.qty),
  }));
};

/* The button both screens draw. The screen sends the rows it worked out and the server lays them
   out as the workbook's sheet — the same split as the project export, so the arithmetic lives in
   pricingModel.js alone. */
export const CbdPartnerOfferButton = ({ projectId, skus, rules, decided, approved, apiBase }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  if (!skus || !skus.some((sk) => isCbd(sk && sk.values))) return null;
  const rows = decided ? cbdOfferRows(skus, rules, decided) : null;
  const why = !projectId ? t('pricing.cbdOfferNeedSave')
    : (!approved ? t('pricing.cbdOfferNeedApproval')
      : (!decided ? t('pricing.cbdOfferNeedCard') : (!rows ? t('pricing.cbdOfferTerms') : '')));
  const go = async () => {
    setBusy(true); setErr('');
    try {
      const v = (skus[0] && skus[0].values) || {};
      const r = await fetch(`${apiBase}/projects/${projectId}/partner-offer-xlsx`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          card: decided,
          rows,
          meta: {
            partner: v.partnerName || '', season: v.offerSeason || '', term: v.priceTerm ?? 'FOB HCM, Vietnam',
            validFrom: v.validFrom || '', validTo: v.validTo || '', xfDate: v.xfDate || '', currency: 'USD',
          },
        }),
      });
      if (!r.ok) {
        let m = `HTTP ${r.status}`;
        try { m = (await r.json()).error || m; } catch (e) { /* keep the status */ }
        throw new Error(m);
      }
      const blob = await r.blob();
      const name = (r.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || `${projectId} - Partner Offer.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <span className="cbd-offer-btn">
      <button type="button" className="sp-btn" disabled={busy || !!why} title={why || t('pricing.cbdOfferTitle')} onClick={go}>
        {t('pricing.cbdOffer')}
      </button>
      {!!why && <em>{why}</em>}
      {!!err && <em className="err">{err}</em>}
    </span>
  );
};
